import { ApiError, auditApi, getToken } from './api';

/**
 * Sends failures seen in this browser to the audit log, so the superadmin sees
 * a crash on somebody's screen next to the server's own errors.
 *
 * Only for a signed-in person (the API takes reports from no one else), at
 * most once a minute for the same failure and twenty times per page load, so
 * a loop that throws cannot flood the log. Reporting never throws: a failure
 * to report is not worth a second one.
 */

const MAX_PER_LOAD = 20;
const SAME_AGAIN_MS = 60_000;

const lastSent = new Map<string, number>();
let sent = 0;

export function reportError(source: 'render' | 'error' | 'rejection', error: unknown, componentStack?: string) {
  if (!getToken() || sent >= MAX_PER_LOAD) return;

  // The server already knows about its own failures, and an unreachable
  // server cannot be told about anything.
  if (error instanceof ApiError) return;

  const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : String(error));
  const message = err.message || 'Unknown error';

  const key = `${source}:${message}`;
  const now = Date.now();
  if (now - (lastSent.get(key) ?? 0) < SAME_AGAIN_MS) return;
  lastSent.set(key, now);
  sent += 1;

  const stack = [err.stack, componentStack && `Component stack:${componentStack}`].filter(Boolean).join('\n\n');

  auditApi
    .reportClientError({
      message: message.slice(0, 500),
      stack: stack.slice(0, 8000),
      url: window.location.pathname + window.location.search,
      source,
    })
    .catch(() => undefined);
}

/** Listens for what nothing else caught. Returns the way to stop listening. */
export function installErrorReporting() {
  const onError = (e: ErrorEvent) => reportError('error', e.error ?? e.message);
  const onRejection = (e: PromiseRejectionEvent) => reportError('rejection', e.reason);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
